/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { useClerk } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as z from 'zod';

import { useCheckOrg, useMutate } from '../net/commons';

import { OrganisationData } from '@/commons/commons';
import { Project, TestSuite } from '@/commons/protocols';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const formSchema = z.object({
    name: z.string().min(3, { message: 'Organisation name must be at least 3 characters long' }),
});
type CreateOrganisationResponse = {
    organisation: OrganisationData;
    project: Project;
    suite: TestSuite;
    error?: { message: string };
};

export default function OrgSetup() {
    const navigate = useNavigate();
    const { signOut } = useClerk();
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm<z.infer<typeof formSchema>>({
        defaultValues: {
            name: '',
        },
        resolver: zodResolver(formSchema),
    });
    const { trigger } = useMutate<CreateOrganisationResponse>(`/bff/organisation`, {
        revalidate: false,
        method: 'POST',
        populateCache: (mutation: CreateOrganisationResponse, org: OrganisationData) => {
            return { ...org, ...mutation.organisation };
        },
    });
    const { error: orgError, isLoading: orgIsLoading, organisation } = useCheckOrg();

    useEffect(() => {
        if (organisation && !orgIsLoading && !orgError) {
            navigate('/');
        }
    }, [organisation, orgIsLoading, orgError]);

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);
            const res = await trigger({ name: values.name });
            navigate(`/suite/${res.project.slug}/${res.suite.slug}/stories`);
        } catch (err) {
            setIsLoading(false);
            form.setError('name', { message: 'There was a problem with your request' });
        }
    };

    if (orgIsLoading) return null;

    return (
        <main className="max-w-md w-full px-6 mx-auto my-10">
            <h1 className="text-2xl font-semibold text-center mb-8">Create a new organisation</h1>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row gap-2 items-center">Organisation name</FormLabel>
                                <FormControl>
                                    <Input placeholder="Write the name" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex flex-row justify-between">
                        <Button type="button" variant="link" onClick={() => signOut({ redirectUrl: '/' })}>
                            Log Out
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? 'Creating Organisation...' : 'Create Organisation'}
                        </Button>
                    </div>
                </form>
            </Form>
        </main>
    );
}
